import { useState, useEffect, useRef } from 'react'
import { QrCode, Scan, Loader2, AlertCircle } from 'lucide-react'
import { useI18n } from '../i18n'
import type { QRCodeErrorCorrectionLevel } from 'qrcode'
import type { QRType } from '../lib/qrUtils'

interface PreviewPanelProps {
  dataUrl: string

  content: string
  error: string | null
  isGenerating: boolean
  level: QRCodeErrorCorrectionLevel
  type: QRType
}

export function PreviewPanel({
  dataUrl,

  content,
  error,
  isGenerating,
  level,
  type,
}: PreviewPanelProps) {
  const { t } = useI18n()
  const [pulse, setPulse] = useState(false)
  const prevDataUrlRef = useRef(dataUrl)

  // Scale pulse animation when QR data changes
  useEffect(() => {
    if (dataUrl && dataUrl !== prevDataUrlRef.current) {
      setPulse(true)
      const timer = setTimeout(() => setPulse(false), 200)
      prevDataUrlRef.current = dataUrl
      return () => clearTimeout(timer)
    }
    prevDataUrlRef.current = dataUrl
  }, [dataUrl])

  const hasContent = content.trim().length > 0
  const showQr = dataUrl && !isGenerating && !error
  const showEmpty = !hasContent && !isGenerating && !error

  return (
    <section className="w-full bg-white rounded-xl p-6 shadow-sm border border-border flex flex-col items-center justify-center min-h-[360px] relative md:sticky md:top-6">
      {/* Section title */}
      <div className="w-full flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          {t('qr.preview.title')}
        </h2>
        <span className="text-xs text-muted-foreground">
          {t('qr.preview.status')}
        </span>
      </div>

      {/* QR display area */}
      <div className="relative w-full flex items-center justify-center flex-1">
        {/* Empty state */}
        {showEmpty && (
          <div className="flex flex-col items-center justify-center text-muted-foreground gap-3 py-12">
            <QrCode className="w-16 h-16 opacity-30" />
            <p className="text-sm font-medium">{t('qr.preview.empty')}</p>
          </div>
        )}

        {/* Generating spinner */}
        {isGenerating && (
          <div className="flex flex-col items-center justify-center text-muted-foreground gap-3 py-12">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="text-sm font-medium">{t('qr.preview.generating')}</p>
          </div>
        )}

        {/* Error state */}
        {error && !isGenerating && (
          <div className="flex flex-col items-center justify-center text-destructive gap-3 py-12 px-4">
            <AlertCircle className="w-10 h-10" />
            <p className="text-sm font-medium text-center">{error}</p>
          </div>
        )}

        {/* QR code image */}
        {showQr && (
          <div className="relative flex items-center justify-center">
            {/* Error correction badge */}
            <span className="absolute -top-2 -right-2 z-10 px-2 py-0.5 bg-muted rounded-md text-xs font-medium text-muted-foreground border border-border">
              {t('qr.preview.errorCorrection', { level })}
            </span>

            {/* QR image with pulse animation */}
            <div
              className="bg-white border border-border rounded-lg p-4 transition-transform"
              style={{
                transform: pulse ? 'scale(0.98)' : 'scale(1)',
                transitionDuration: '200ms',
                transitionTimingFunction: 'ease-out',
              }}
            >
              <img
                src={dataUrl}
                alt={`QR code for ${type}`}
                className="w-full h-auto"
                style={{
                  minWidth: '256px',
                  maxWidth: 'min(400px, 100%)',
                  width: '100%',
                }}
              />
            </div>

            {/* Test scan button */}
            <button
              onClick={() => {
                // Placeholder for scan test functionality
                // Could open a modal with device camera or show a hint
                if (dataUrl) {
                  const w = window.open('', '_blank')
                  if (w) {
                    w.document.write(`
                      <html>
                        <head><title>QR Test Scan</title></head>
                        <body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8fafc;">
                          <div style="text-align:center;">
                            <img src="${dataUrl}" style="max-width:300px;" />
                            <p style="margin-top:16px;font-family:sans-serif;color:#64748b;">Scan this QR code with your phone</p>
                          </div>
                        </body>
                      </html>
                    `)
                  }
                }
              }}
              className="absolute -bottom-3 -right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground bg-background border border-border rounded-lg hover:bg-muted transition-colors shadow-sm"
              title="Test scan"
            >
              <Scan className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t('qr.preview.status')}</span>
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
